import { useEffect, useState } from "react";
import EmbedYoutubeVideo from "./EmbedYoutubeVideo";
import { useAppContext } from "../hooks/useAppContext";
import { ChatResponse } from "../api/models";
import { thumbsApi } from "../api/chatAPI";
import useBoundStore from "../store/useBoundStore";
import ThumbsUp from "./SVGIcons/ThumbsUp";
import ThumbsDown from "./SVGIcons/ThumbsDown";
import ModalFeedback from "./ModalFeedback";
import ChatProfile from "./ChatProfile";
import Glitter from "./SVGIcons/Glitter";
import Smile from "./SVGIcons/Smile";
import ChatBubble from "./SVGIcons/ChatBubble";
import { Locale } from "../context/AppProvider";
import Markdown from "./Markdown";
import { getTranslation } from "../locales/getTranslation";
import Video from "./SVGIcons/Video";
import useCopyToClipboard from "../hooks/useCopyToClipboard";
import Tooltip from "./Tooltip";
import Copy from "./SVGIcons/Copy";
import EmbedBilibiliVideo from "./EmbedBilibiliVideo";

interface Props {
  answer: ChatResponse;
  isSelected?: boolean;
  onCitationClicked?: (filePath: string) => void;
  onThoughtProcessClicked?: () => void;
  onSupportingContentClicked?: () => void;
  onFollowupQuestionClicked: (question: string) => void;
  showFollowupQuestions?: boolean;
  redoButton?: JSX.Element;
}

export enum Thumb {
  Up = "UP",
  Down = "DOWN",
}

export const Answer = ({
  answer,
  onFollowupQuestionClicked,
  redoButton,
}: Props) => {
  const { brand, locale, theme, ipGeoLocation } = useAppContext();
  const updateAnswerFeedback = useBoundStore((state) => state.updateAnswerFeedback);
  const [feedbackOpen, setFeedbackOpen] = useState<boolean>(false);
  const [thumbUp, setThumbUp] = useState<boolean>((answer.thumbs_up ?? 0) > 0);
  const [thumbDown, setThumbDown] = useState<boolean>(
    (answer.thumbs_down ?? 0) > 0,
  );
  const [expandFeedbackUI, setExpandFeedbackUI] = useState(false);
  const [thumbsUpHover, setThumbsUpHover] = useState(false);
  const [thumbsDownHover, setThumbsDownHover] = useState(false);
  const [isCopied, copy] = useCopyToClipboard();
  const isMobile = typeof window !== "undefined" && window.innerWidth < 640;

  const onThumbUpClicked = async (_id: string) => {
    try {
      if (thumbUp) {
        await thumbsApi(brand, _id, 0, 0, [], "");
        updateAnswerFeedback(_id, 0, 0, [], "");
      } else {
        await thumbsApi(brand, _id, 1, 0, [], "");
        updateAnswerFeedback(_id, 1, 0, [], "");
      }
      setThumbUp(!thumbUp);
      setThumbDown(false);
    } catch (e) {
      // console.log(e);
    }
  };

  const onThumbDownClicked = async (
    _id: string,
    feedbackChoices: string[],
    feedbackDetails: string,
  ) => {
    try {
      await thumbsApi(brand, _id, 0, 1, feedbackChoices, feedbackDetails);
      updateAnswerFeedback(_id, 0, 1, feedbackChoices, feedbackDetails);
      setThumbUp(false);
      setThumbDown(true);
    } catch (e) {
      // console.log(e);
    }
  };

  const onUndoVote = async () => {
    try {
      await thumbsApi(brand, answer._id, 0, 0, [], "");
      updateAnswerFeedback(answer._id, 0, 0, [], "");
      setThumbDown(false);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    setThumbUp((answer.thumbs_up ?? 0) > 0);
    setThumbDown((answer.thumbs_down ?? 0) > 0);
  }, [answer.thumbs_up, answer.thumbs_down]);

  useEffect(() => {
    setExpandFeedbackUI(true);
  }, []);

  useEffect(() => {
    if (expandFeedbackUI) {
      setTimeout(() => {
        setExpandFeedbackUI(false);
      }, 5000);
    }
  }, [expandFeedbackUI]);

  return (
    <>
      <div className="flex w-full flex-col items-start justify-center gap-2.5 py-8">
        <ChatProfile />
        <div
          className={`flex w-full min-w-0 flex-col items-start justify-start gap-4 ${theme === "dark" ? "bg-grayscale-solid-g1" : "bg-grayscale-solid-g0"} p-4`}
        >
          {answer.answer && <Markdown answer={answer} />}

          {!!answer.community_message && (
            <div
              className="whitespace-pre-wrap text-sm font-semibold leading-snug text-grayscale-solid-g10 sm:text-[15px] [&>a]:text-blue-solid-blue6"
              dangerouslySetInnerHTML={{ __html: answer.community_message }}
            />
          )}

          {!!answer.suggested_articles?.length && (
            <>
              <div className="h-px w-full bg-gray-200" />
              <div className="flex w-full flex-col gap-1.5">
                <div className="flex h-6 items-center justify-start gap-2.5 py-1.5">
                  <span className="text-xs font-bold leading-[14px] text-grayscale-solid-g10 sm:text-sm">
                    {getTranslation(locale, "Suggested Articles")}
                  </span>
                  <Glitter className="h-3 w-3 fill-grayscale-solid-g10" />
                </div>
                <ul className="flex flex-wrap justify-start gap-1.5">
                  {answer.suggested_articles.map((x, i) => {
                    return (
                      <li className="group rounded-[30px]" key={i}>
                        <a
                          className="inline-flex cursor-pointer items-center justify-center rounded-[30px] px-3.5 py-2 text-xs font-medium leading-[17px] text-grayscale-solid-g10 no-underline outline outline-1 outline-offset-[-1px] outline-grayscale-opacity-blevel3 group-hover:bg-grayscale-solid-g2 group-hover:!text-grayscale-solid-g10 sm:text-sm"
                          href={x.url}
                          target="_blank"
                        >
                          {`${x.title}`}
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </>
          )}

          {!!answer.suggested_videos?.length && (
            <>
              <div className="h-px w-full bg-gray-200" />
              <div className="flex w-full flex-col gap-1.5">
                <div className="flex h-6 items-center justify-start gap-2.5 py-1.5">
                  <span className="text-xs font-bold leading-[14px] text-grayscale-solid-g10 sm:text-sm">
                    {getTranslation(locale, "Suggested Videos")}
                  </span>
                  <Video className="h-3 w-3 fill-grayscale-solid-g10" />
                </div>
                <ul className="grid w-full grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3">
                  {answer.suggested_videos.map((x, i) => {
                    return x.url.includes("bilibili") ? (
                      <li className="rounded-[10px] bg-slate-100" key={i}>
                        <EmbedBilibiliVideo key={i} url={x.url} />
                      </li>
                    ) : (
                      <li className="rounded-[10px] bg-slate-100" key={i}>
                        <EmbedYoutubeVideo key={i} url={x.url} />
                      </li>
                    );
                  })}
                </ul>
              </div>
            </>
          )}

          {!!answer?.follow_up_questions?.length && (
            <>
              <div className="h-px w-full bg-gray-200" />
              <div className="flex w-full flex-col gap-1.5">
                <div className="flex h-6 items-center justify-start gap-2.5 py-1.5">
                  <span className="text-xs font-bold leading-[14px] text-grayscale-solid-g10 sm:text-sm">
                    {getTranslation(locale, "Follow-up Questions")}
                  </span>
                  <ChatBubble className="h-3 w-3 fill-grayscale-solid-g10" />
                </div>
                <ul className="flex flex-wrap justify-start gap-1.5">
                  {answer.follow_up_questions.map((x, i) => {
                    return (
                      <li key={i} className="group rounded-[30px]">
                        <a
                          className="inline-flex cursor-pointer items-center justify-center rounded-[30px] px-3.5 py-2 text-xs font-medium leading-[17px] text-grayscale-solid-g10 no-underline outline outline-1 outline-offset-[-1px] outline-grayscale-opacity-blevel3 group-hover:bg-grayscale-solid-g2 group-hover:!text-grayscale-solid-g10 sm:text-sm"
                          title={x}
                          onClick={() => onFollowupQuestionClicked(x)}
                          target="_blank"
                        >
                          {`${x}`}
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </>
          )}

          {/* Feedback */}
          <div className="flex w-full items-center justify-between gap-2.5">
            <ul
              className={`relative flex items-start justify-start gap-[4px] rounded-2xl bg-opacity-level1 px-2.5 py-1`}
              onMouseLeave={() => {
                setThumbsUpHover(false);
                setThumbsDownHover(false);
              }}
            >
              <li
                className={`group z-[1] flex h-[30px] w-[30px] cursor-pointer items-center justify-center rounded-full p-1.5 ${thumbUp ? "" : ""}`}
                onClick={() => {
                  onThumbUpClicked(answer._id);
                }}
                onMouseEnter={() => {
                  if (expandFeedbackUI == false) {
                    setThumbsUpHover(true);
                    setThumbsDownHover(false);
                  }
                }}
              >
                <ThumbsUp
                  className={`size-4 cursor-pointer ${thumbUp ? "fill-blue-solid-blue6" : "fill-grayscale-solid-g10 group-hover:fill-blue-solid-blue6"}`}
                />
              </li>
              <li
                className={`flex h-[30px] items-center justify-center gap-2 font-medium text-grayscale-solid-g10 transition-all ${!isMobile && (thumbsUpHover || expandFeedbackUI) ? (locale == Locale.English ? "w-[240px]" : locale == Locale.Korean ? "w-[220px]" : locale == Locale.Chinese ? "mr-1 w-[160px]" : locale == Locale.Japanese ? "ml-1 mr-2 w-[185px]" : locale == Locale.Spanish ? "w-[195px]" : locale == Locale.Portuguese ? "w-[175px]" : locale == Locale.French ? "w-[205px]" : "w-0") : "w-0"} ${expandFeedbackUI ? "delay-500" : "delay-[25ms]"} ease-linear`}
              >
                <span
                  className={`transition-opacity ${!isMobile && (thumbsUpHover || expandFeedbackUI) ? "opacity-100" : "opacity-0"} ${thumbsUpHover && !expandFeedbackUI ? "delay-100" : ""} ${expandFeedbackUI ? "delay-700" : ""} whitespace-nowrap`}
                >
                  {getTranslation(locale, "Thumbs Up Message")}
                </span>
                <Glitter className="h-4 w-4 fill-grayscale-solid-g10" />
              </li>

              <li className={`flex h-[30px] items-center justify-center`}>
                <div className="h-5 w-[1px] bg-grayscale-opacity-blevel3" />
              </li>

              <li
                className={`group z-[1] ml-[3px] flex h-[30px] w-[30px] cursor-pointer items-center justify-center rounded-full ${thumbDown ? "" : ""}`}
                onClick={() => {
                  setFeedbackOpen(true);
                }}
                onMouseEnter={() => {
                  if (expandFeedbackUI == false) {
                    setThumbsUpHover(false);
                    setThumbsDownHover(true);
                  }
                }}
              >
                <ThumbsDown
                  className={`size-4 cursor-pointer ${thumbDown ? "fill-blue-solid-blue6" : "fill-grayscale-solid-g10 group-hover:fill-blue-solid-blue6"}`}
                />
              </li>
              <li
                className={`flex h-[30px] items-center justify-center gap-2 font-medium text-grayscale-solid-g10 transition-all ${!isMobile && thumbsDownHover ? (locale == Locale.English ? "mx-1 w-[220px]" : locale == Locale.Korean ? "w-[205px]" : locale == Locale.Chinese ? "mx-1 w-[175px]" : locale == Locale.Japanese ? "mx-1 w-[300px]" : locale == Locale.Spanish ? "mx-1 w-[290px]" : locale == Locale.Portuguese ? "w-[280px]" : locale == Locale.French ? "w-[315px]" : "w-0") : "w-0"} delay-[100ms] ease-linear`}
              >
                <span
                  className={`transition-opacity ${!isMobile && thumbsDownHover ? "opacity-100 delay-[150ms]" : "opacity-0"} whitespace-nowrap`}
                >
                  {getTranslation(locale, "Thumbs Down Message")}
                </span>
                <Smile className="h-4 w-4 fill-grayscale-solid-g10" />
              </li>
              <li className="flex h-[30px] items-center justify-center">
                <div className="h-5 w-[1px] bg-grayscale-opacity-blevel3" />
              </li>
              <li
                className={`z-[1] ml-[3px] flex h-[30px] w-[30px] cursor-pointer items-center justify-center rounded-xl p-1.5`}
              >
                {redoButton}
              </li>
              <li className="flex h-[30px] items-center justify-center">
                <div className="h-5 w-[1px] bg-grayscale-opacity-blevel3" />
              </li>
              <li
                className="z-[1] ml-[3px] flex h-[30px] w-[30px] cursor-pointer items-center justify-center rounded-xl p-1.5"
                onClick={() => {
                  copy(answer.answer);
                }}
              >
                <Tooltip
                  text={`${isCopied ? "Copied!" : "Copy"}`}
                  position="bottom"
                >
                  <Copy
                    className="h-4 w-4 fill-grayscale-solid-g10 hover:fill-blue-solid-blue6"
                    onClick={() => copy(answer.answer)}
                  />
                </Tooltip>
              </li>
            </ul>

            {(import.meta.env.MODE === "dev" ||
              import.meta.env.MODE === "localhost") &&
              answer?.response_time && (
                <p className="text-base font-bold text-grayscale-solid-g10">
                  Response time: {answer?.response_time} secs
                </p>
              )}
          </div>
        </div>
      </div>

      <ModalFeedback
        _id={answer._id}
        feedbackOpen={feedbackOpen}
        setFeedbackOpen={setFeedbackOpen}
        onThumbDownClicked={onThumbDownClicked}
        initialChoices={answer.feedback_choices ?? []}
        initialDetails={answer.feedback_details ?? ""}
        onUndoVote={thumbDown ? onUndoVote : undefined}
      />
    </>
  );
};
